/**
 * ============================================================================
 * KbPageLinkPicker — Modal para escolher um artigo do tenant e inserir link.
 *                    Usado pelo slash menu, atalho `[[` e bubble menu.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial.
 * ============================================================================
 */
import { useEffect, useMemo, useState } from "react";
import { Search, FileText, Loader2, Globe2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useKbSearchPagesForLink, type KbLinkSearchResult } from "@/hooks/kb/useKbSearchPagesForLink";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Space atual — usado como filtro padrão. */
  currentSpaceId?: string | null;
  /** Callback com a página escolhida (o consumidor decide como inserir a mark). */
  onPick: (page: KbLinkSearchResult) => void;
}

const STATUS_BADGE: Record<string, { label: string; variant: "default" | "secondary" | "outline" }> = {
  published: { label: "publicado", variant: "secondary" },
  approved:  { label: "aprovado",  variant: "outline" },
  in_review: { label: "em revisão", variant: "outline" },
  draft:     { label: "rascunho",  variant: "outline" },
};

export function KbPageLinkPicker({ open, onOpenChange, currentSpaceId, onPick }: Props) {
  const [query, setQuery] = useState("");
  const [allSpaces, setAllSpaces] = useState(false);
  const [selected, setSelected] = useState(0);

  const spaceFilter = allSpaces ? null : (currentSpaceId ?? null);
  const { data, isLoading } = useKbSearchPagesForLink(query, spaceFilter, 20);
  const results = data ?? [];

  // Reset quando abre
  useEffect(() => {
    if (open) {
      setQuery("");
      setSelected(0);
    }
  }, [open]);

  useEffect(() => setSelected(0), [results.length, allSpaces]);

  const pick = (r: KbLinkSearchResult) => {
    onPick(r);
    onOpenChange(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelected((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelected((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && results[selected]) {
      e.preventDefault();
      pick(results[selected]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl p-0 gap-0">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="text-sm font-semibold">Link para artigo</DialogTitle>
        </DialogHeader>

        <div className="px-4 pb-3 space-y-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Buscar por título ou slug…"
              className="pl-8 h-9 text-sm"
            />
          </div>

          {currentSpaceId && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Switch id="all-spaces" checked={allSpaces} onCheckedChange={setAllSpaces} />
              <Label htmlFor="all-spaces" className="cursor-pointer flex items-center gap-1">
                <Globe2 className="h-3 w-3" />
                Buscar em todos os spaces do tenant
              </Label>
            </div>
          )}
        </div>

        <div className="max-h-[400px] overflow-y-auto border-t">
          {isLoading && (
            <div className="flex items-center justify-center py-8 text-xs text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Buscando…
            </div>
          )}
          {!isLoading && results.length === 0 && (
            <div className="py-8 text-xs text-muted-foreground text-center">
              {query.trim() ? "Nenhum artigo encontrado." : "Digite para buscar artigos."}
            </div>
          )}
          {results.map((r, idx) => {
            const active = idx === selected;
            const st = STATUS_BADGE[r.status];
            return (
              <button
                key={r.page_id}
                type="button"
                onMouseEnter={() => setSelected(idx)}
                onClick={() => pick(r)}
                className={`w-full text-left px-4 py-2 flex items-start gap-2 border-b last:border-b-0 transition-colors ${
                  active ? "bg-accent" : "hover:bg-accent/50"
                }`}
              >
                <FileText className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{r.title}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {r.space_name}
                    {r.collection_name ? ` › ${r.collection_name}` : ""}
                  </div>
                </div>
                {st && (
                  <Badge variant={st.variant} className="text-[10px] shrink-0">
                    {st.label}
                  </Badge>
                )}
              </button>
            );
          })}
        </div>

        <div className="px-4 py-2 border-t bg-muted/30 text-[11px] text-muted-foreground flex items-center justify-between">
          <span>↑↓ navegar · Enter inserir · Esc fechar</span>
          <span>{results.length} resultado{results.length === 1 ? "" : "s"}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
